import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { sessionService } from '../api/services';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { PhoneOff, MessageCircle, X, Send, AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

/* ─── Chat Panel ─────────────────────────────────── */
const ChatPanel = ({ sessionId, open, onClose }) => {
  const [message, setMessage] = useState('');
  const { data, loading, refetch } = useApi(() => sessionService.getChatHistory(sessionId), [sessionId]);
  const messages = data?.messages ?? [];
  const { user } = useAuth();
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => {
    if (!open) return;
    const id = setInterval(refetch, 5000);
    return () => clearInterval(id);
  }, [open, refetch]);

  if (!open) return null;

  return (
    <div className="absolute inset-y-0 right-0 w-72 bg-white border-l border-cream-200 flex flex-col z-20 shadow-xl">
      <div className="flex items-center justify-between px-4 py-3 border-b border-cream-100">
        <span className="font-sans font-semibold text-forest-900 text-sm">Session Chat</span>
        <button onClick={onClose} className="text-forest-400 hover:text-forest-700"><X size={16} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading && <div className="text-center text-xs text-forest-400 py-4">Loading...</div>}
        {!loading && messages.length === 0 && <div className="text-center text-xs text-forest-400 py-4">No messages yet</div>}
        {messages.map((msg, i) => {
          const isMe = msg.senderId === user?.id;
          return (
            <div key={i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-xs leading-relaxed ${isMe ? 'bg-forest-700 text-cream-50 rounded-tr-sm' : 'bg-cream-100 text-forest-800 rounded-tl-sm'}`}>
                {!isMe && <div className="font-semibold text-forest-600 mb-0.5 text-[10px]">{msg.senderName}</div>}
                {msg.content}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={(e) => e.preventDefault()} className="p-3 border-t border-cream-100 flex gap-2">
        <input type="text" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Type a message..."
          className="flex-1 px-3 py-2 rounded-xl border border-cream-200 text-xs focus:outline-none focus:border-forest-400 bg-cream-50" />
        <button type="submit" disabled={!message.trim()}
          className="w-8 h-8 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 hover:bg-forest-800 disabled:opacity-40 transition-colors flex-shrink-0">
          <Send size={13} />
        </button>
      </form>
    </div>
  );
};

/* ─── Daily.co iframe component ──────────────────── */
const DailyRoom = ({ roomUrl, token, sessionType, onLeave, onJoined }) => {
  const containerRef = useRef(null);
  const frameRef     = useRef(null);

  useEffect(() => {
    if (!roomUrl || !token || !containerRef.current) return;

    const loadAndJoin = () => {
      return new Promise((resolve) => {
        if (window.DailyIframe) { resolve(); return; }
        const script  = document.createElement('script');
        script.src    = 'https://unpkg.com/@daily-co/daily-js';
        script.async  = true;
        script.onload = resolve;
        document.head.appendChild(script);
      });
    };

    loadAndJoin().then(() => {
      if (frameRef.current) { frameRef.current.destroy(); frameRef.current = null; }

      frameRef.current = window.DailyIframe.createFrame(containerRef.current, {
        iframeStyle: { width: '100%', height: '100%', border: 'none' },
        showLeaveButton:      true,
        showFullscreenButton: true,
        startVideoOff:        sessionType === 'AUDIO',
        startAudioOff:        false,
      });

      frameRef.current
        .on('joined-meeting', () => onJoined?.())
        .on('left-meeting',   () => onLeave?.())
        .on('error', (e) => {
          console.error('Daily error:', e);
          toast.error('Connection error. Please refresh and try again.');
        });

      frameRef.current.join({ url: roomUrl, token });
    });

    return () => {
      if (frameRef.current) { frameRef.current.destroy(); frameRef.current = null; }
    };
  }, [roomUrl, token, sessionType]);

  return <div ref={containerRef} className="flex-1 w-full" style={{ minHeight: 0 }} />;
};

/* ─── Main Page ──────────────────────────────────── */
const SessionRoomPage = () => {
  const { id }       = useParams();
  const { user }     = useAuth();
  const navigate     = useNavigate();
  const [chatOpen,   setChatOpen]   = useState(false);
  const [ending,     setEnding]     = useState(false);
  const [completing, setCompleting] = useState(false);

  const { data: sessionData, loading, error } = useApi(() => sessionService.getById(id), [id]);
  const { data: tokenData, loading: tokenLoading, error: tokenError } = useApi(
    () => sessionService.getJoinToken(id), [id]
  );

  const session     = sessionData?.session ?? sessionData;
  const token       = tokenData?.token   ?? null;
  const roomUrl     = tokenData?.roomUrl ?? null;
  const sessionType = session?.sessionType;
  const isExpert    = user?.role === 'EXPERT';

  const handleEnd = async () => {
    if (!window.confirm('End this session for all participants?')) return;
    setEnding(true);
    try {
      await sessionService.end(id);
      toast.success('Session ended. Transcription will begin shortly.');
      navigate('/expert/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not end session');
      setEnding(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await sessionService.markComplete(id);
      toast.success('Session marked as complete!');
      // Experts go to their dashboard, clients go to progress
      navigate(isExpert ? '/expert/dashboard' : '/progress');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not mark session complete');
      setCompleting(false);
    }
  };

  if (loading || tokenLoading) return (
    <div className="min-h-screen bg-forest-950 flex items-center justify-center">
      <div className="text-center space-y-3">
        <Loader2 size={36} className="animate-spin text-forest-400 mx-auto" />
        <p className="text-forest-400 text-sm">Preparing your session room...</p>
      </div>
    </div>
  );

  if (error || tokenError || !session) return (
    <div className="min-h-screen bg-forest-950 flex items-center justify-center px-4">
      <div className="text-center space-y-4 max-w-md">
        <AlertCircle size={40} className="text-rust-400 mx-auto" />
        <h2 className="font-serif text-2xl text-cream-50">Session not found</h2>
        <p className="text-forest-400 text-sm">This session may have ended or you don't have access.</p>
        <Link to="/dashboard" className="btn-primary inline-flex">← Back to Dashboard</Link>
      </div>
    </div>
  );

  return (
    <div className="h-screen bg-gray-950 flex flex-col overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-gray-900 border-b border-gray-800 flex-shrink-0">
        <div>
          <div className="text-cream-50 font-sans font-semibold text-sm">{sessionType} Session</div>
          <div className="text-gray-400 text-xs mt-0.5 flex items-center gap-1.5">
            {session.status === 'LIVE' && <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse inline-block" />}
            {session.status}
            {session.booking?.duration && ` · ${session.booking.duration} mins`}
          </div>
        </div>
        <Link to="/dashboard" className="text-gray-400 hover:text-cream-50 transition-colors"><X size={20} /></Link>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col overflow-hidden relative">

        {/* CHAT */}
        {sessionType === 'CHAT' && (
          <div className="flex-1 relative flex bg-gray-950">
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-forest-800 rounded-full flex items-center justify-center mx-auto">
                  <MessageCircle size={32} className="text-forest-300" />
                </div>
                <p className="text-gray-400 text-sm">Chat session in progress</p>
                <button onClick={() => setChatOpen(true)} className="btn-primary mx-auto flex items-center gap-2">
                  <MessageCircle size={15} /> Open Chat
                </button>
              </div>
            </div>
            <ChatPanel sessionId={id} open={chatOpen} onClose={() => setChatOpen(false)} />
          </div>
        )}

        {/* VIDEO / AUDIO — Daily.co embed */}
        {(sessionType === 'VIDEO' || sessionType === 'AUDIO') && (
          token && roomUrl
            ? <DailyRoom
                roomUrl={roomUrl}
                token={token}
                sessionType={sessionType}
                onJoined={() => console.log('Joined Daily room')}
                onLeave={() => toast('You left the session.', { icon: '👋' })}
              />
            : <div className="flex-1 flex items-center justify-center bg-gray-950">
                <div className="text-center space-y-3">
                  <Loader2 size={32} className="animate-spin text-forest-400 mx-auto" />
                  <p className="text-gray-400 text-sm">Connecting to session room...</p>
                </div>
              </div>
        )}
      </div>

      {/* Footer controls */}
      <div className="bg-gray-900 border-t border-gray-800 px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          {sessionType === 'CHAT' && (
            <button onClick={() => setChatOpen(!chatOpen)}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${chatOpen ? 'bg-forest-600 text-white' : 'bg-gray-700 text-cream-50 hover:bg-gray-600'}`}>
              <MessageCircle size={18} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Both client AND expert can mark the session complete */}
          {session.status === 'LIVE' && (
            <button onClick={handleComplete} disabled={completing}
              className="btn-primary text-sm py-2 px-5 disabled:opacity-60 flex items-center gap-2">
              {completing
                ? <><Loader2 size={14} className="animate-spin" /> Completing...</>
                : '✓ Mark Complete'}
            </button>
          )}
          {/* Expert also has End Session to close the room for everyone */}
          {isExpert && session.status === 'LIVE' && (
            <button onClick={handleEnd} disabled={ending}
              className="flex items-center gap-2 bg-rust-500 hover:bg-rust-600 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-colors disabled:opacity-60">
              {ending ? <Loader2 size={14} className="animate-spin" /> : <PhoneOff size={15} />}
              End Session
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionRoomPage;